import { GA4RestClient } from './GA4RestClient';

/**
 * Transforma eventos en formato Universal Analytics (UA) al payload
 * que espera el Measurement Protocol REST de GA4.
 *
 * Usado por: src/utils/analytics.js
 *
 * El objeto UA tiene la forma:
 * {
 *   hitType: 'event',
 *   eventAction: '...',
 *   eventCategory: '...',
 *   eventLabel: '...',
 *   dimension1: '',  // country
 *   dimension2: '',  // client_id
 *   dimension3: '',  // user_id
 *   dimension4: '',  // user_type
 *   dimension6: '',  // device
 *   dimension7: '',  // app_version
 *   dimension8: '',  // device_brand
 *   dimension9: ''   // parent_id
 * }
 */

/**
 * Mapa de sustitución para caracteres con tilde/acento del español.
 * Usado en normalizeAccents() para convertir nombres de eventos a ASCII
 * sin depender de String.prototype.normalize (no disponible en PS Browser).
 */
const ACCENT_MAP = {
    'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u',
    'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ú': 'U',
    'ñ': 'n', 'Ñ': 'N', 'ü': 'u', 'Ü': 'U'
};

export class UAEventTransformer {

    /**
     * Reemplaza caracteres acentuados del español por su equivalente ASCII.
     * Evita que los nombres de eventos lleguen con guiones bajos donde debería
     * haber letras (p. ej. "Inicia_sesi_n" → "Inicia_sesion").
     *
     * @param {string} name
     * @returns {string}
     */
    static normalizeAccents(name) {
        return name.replace(/[áéíóúÁÉÍÓÚñÑüÜ]/g, ch => ACCENT_MAP[ch] || ch);
    }

    /**
     * Convierte un string arbitrario a un nombre de evento válido para GA4.
     * El nombre debe comenzar con letra y solo puede contener letras,
     * números y guiones bajos (máx. 40 caracteres).
     *
     * Se normalizan primero los acentos para preservar las letras base
     * (p. ej. "sesión" → "sesion", no "sesi_n").
     *
     * @param {string} name Cadena original (p. ej. eventAction).
     * @returns {string} Nombre sanitizado.
     */
    static sanitizeEventName(name) {
        if (!name) return 'event';
        const sanitized = UAEventTransformer.normalizeAccents(name)
            .toLowerCase()
            .replace(/[^a-zA-Z0-9_]/g, '_')
            .replace(/^[^a-zA-Z]+/, '')
            .substr(0, 40);
        return sanitized || 'event';
    }

    /**
     * Transforma un evento UA al payload del Measurement Protocol REST de GA4.
     * Mapea las dimensiones numéricas (dimensionN) a parámetros con nombre,
     * y extrae client_id / user_id a sus posiciones correctas en el payload.
     *
     * @param {object} event Evento UA recibido por sendGAEvent().
     * @returns {object} Payload listo para enviar con GA4RestClient.send().
     */
    static transform(event) {
        // client_id: se delega a GA4RestClient para obtener el identificador
        // canónico (_ga cookie o device ID). Se pasa dimension2 como candidato
        // pero se descarta si es el placeholder vacío o 'anonymous'.
        const clientId  = GA4RestClient.getClientId(event.dimension2);
        const userId    = event.dimension3 || '';
        // session_id: debe ser número entero (requisito GA4 MP)
        const sessionId = GA4RestClient.getSessionId();

        const payload = {
            client_id: clientId,
            events: [{
                name: UAEventTransformer.sanitizeEventName(event.eventAction || event.hitType || 'event'),
                params: {
                    event_category:       event.eventCategory || '',
                    event_label:          event.eventLabel    || '',
                    // engagement_time_msec debe ser número, no string
                    engagement_time_msec: 1,
                    session_id:           sessionId,
                    country:              event.dimension1    || '',
                    user_type:            event.dimension4    || '',
                    device:               event.dimension6    || '',
                    app_version:          event.dimension7    || '',
                    device_brand:         event.dimension8    || '',
                    parent_id:            event.dimension9    || ''
                }
            }]
        };

        if (userId) {
            payload.user_id = userId;
        }

        return payload;
    }
}



// WEBPACK FOOTER //
// ./src/utils/trackers/ga-api-rest-ps4/UAEventTransformer.js